import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { prefersReducedMotion } from '../motion';

/** Fades content up the first time it scrolls into view. */
export function Reveal({
  children,
  delay = 0,
  fill = false,
}: {
  children: ReactNode;
  delay?: number;
  /** Stretch to the parent's height (cards in a grid row). */
  fill?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === 'undefined',
  );
  useEffect(() => {
    const node = ref.current;
    if (shown || !node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);
  return (
    <Box
      ref={ref}
      sx={{
        height: fill ? '100%' : undefined,
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(24px)',
        transition: `opacity 700ms cubic-bezier(0.2, 0, 0, 1) ${delay}ms, transform 700ms cubic-bezier(0.05, 0.7, 0.1, 1) ${delay}ms`,
      }}
    >
      {children}
    </Box>
  );
}
